import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { getSchemeApplicationById } from '../../api/web';
import moment from 'moment';

const SchemeApplicationDetail = (props) => {
    const id = props.match.params.id;
    const [initialLoading, setInitialLoading] = useState(true);
    const [application, setApplication] = useState(null);

    useEffect(() => {
        getSchemeApplicationById(id).then((response) => {
            if (!response.error) {
                setApplication(response.payload);
            }
            setInitialLoading(false);
        });
    }, [id]);

    const statusBadge = (status) => {
        const map = {
            approved: { cls: 'bg-success text-white', style: {} },
            rejected: { cls: 'bg-danger text-white', style: {} },
            pending: { cls: 'text-white', style: { backgroundColor: '#fd7e14' } }
        };
        const c = map[status] || { cls: 'bg-secondary text-white', style: {} };
        return <span className={`badge ${c.cls}`} style={c.style}>{status || '—'}</span>;
    };

    if (initialLoading) {
        return (
            <div className="content-body">
                <div className="container d-flex justify-content-center ht-100p">
                    <div className="d-flex flex-column align-items-center justify-content-center">
                        <div className="spinner-border" role="status">
                            <span className="sr-only">Loading...</span>
                        </div>
                        <p className="mg-t-10">Loading scheme application</p>
                    </div>
                </div>
            </div>
        );
    }

    if (!application) {
        return (
            <div className="content-body">
                <div className="container pd-x-0">
                    <div className="mg-b-20">
                        <nav aria-label="breadcrumb">
                            <ol className="breadcrumb breadcrumb-style1 mg-b-10">
                                <li className="breadcrumb-item"><Link to="/dashboard">Menu</Link></li>
                                <li className="breadcrumb-item"><Link to="/scheme_application">Scheme Applications</Link></li>
                                <li className="breadcrumb-item active" aria-current="page">Not found</li>
                            </ol>
                        </nav>
                        <p className="tx-danger">Scheme application not found.</p>
                        <Link to="/scheme_application" className="btn btn-sm btn-primary">Back to Scheme Applications</Link>
                    </div>
                </div>
            </div>
        );
    }

    const schemeName = application.scheme?.scheme_name ?? application.scheme_name ?? '—';
    const schemeId = application.scheme?.id ?? application.scheme;
    const farmer = application.farmer;
    const farmerName = farmer
        ? `${farmer.first_name || ''} ${farmer.last_name || ''}`.trim() || farmer.username || '—'
        : '—';
    const farmerId = farmer?.id;
    const answers = Array.isArray(application.answers) ? application.answers : [];

    return (
        <div className="content-body">
            <div className="container pd-x-0">
                <div className="d-sm-flex align-items-center justify-content-between mg-b-20 mg-lg-b-25 mg-xl-b-30">
                    <div>
                        <nav aria-label="breadcrumb">
                            <ol className="breadcrumb breadcrumb-style1 mg-b-10">
                                <li className="breadcrumb-item"><Link to="/dashboard">Menu</Link></li>
                                <li className="breadcrumb-item"><Link to="/scheme_application">Scheme Applications</Link></li>
                                <li className="breadcrumb-item active" aria-current="page">Application #{application.id}</li>
                            </ol>
                        </nav>
                        <h4 className="mg-b-5 tx-spacing--1">Scheme Application #{application.id}</h4>
                        <div className="d-flex flex-wrap align-items-center gap-2">
                            <span className="tx-color-03 tx-13">Scheme: {schemeId ? <Link to={`/scheme/${schemeId}`} className="tx-primary">{schemeName}</Link> : schemeName}</span>
                            {statusBadge(application.application_status)}
                        </div>
                    </div>
                    <div className="d-none d-md-block">
                        <Link to="/scheme_application" className="btn btn-sm pd-x-15 btn-secondary btn-uppercase">
                            <i className="wd-10 mg-r-5 fa fa-arrow-left"></i> Back to list
                        </Link>
                    </div>
                </div>

                <div className="row row-xs">
                    <div className="col-md-12 mg-t-10">
                        <div className="card">
                            <div className="card-header">
                                <h6 className="mg-b-0">Application details</h6>
                            </div>
                            <div className="card-body">
                                <div className="row row-sm">
                                    <div className="col-md-6">
                                        <p><strong>ID:</strong> {application.id}</p>
                                        <p><strong>Scheme:</strong> {schemeId ? <Link to={`/scheme/${schemeId}`} className="tx-primary">{schemeName}</Link> : schemeName}</p>
                                        <p><strong>Farmer:</strong> {farmerId ? <Link to={`/user/${farmerId}`} className="tx-primary">{farmerName}</Link> : farmerName}</p>
                                        <p><strong>Status:</strong> {statusBadge(application.application_status)}</p>
                                    </div>
                                    <div className="col-md-6">
                                        <p><strong>Date added:</strong> {application.date_added ? moment(application.date_added).format('Do MMM, YYYY HH:mm') : '—'}</p>
                                        <p><strong>Date approved:</strong> {application.date_approved ? moment(application.date_approved).format('Do MMM, YYYY HH:mm') : '—'}</p>
                                    </div>
                                </div>

                                {answers.length > 0 && (
                                    <>
                                        <hr className="mg-t-20 mg-b-20" />
                                        <h6 className="mg-b-15">Application answers</h6>
                                        <div className="table-responsive">
                                            <table className="table table-bordered table-sm">
                                                <thead>
                                                    <tr>
                                                        <th>Question / Prerequisite</th>
                                                        <th>Answer / Value</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {answers.map((item, idx) => {
                                                        const questionLabel = typeof item.question === 'object' && item.question != null
                                                            ? (item.question.label ?? item.question.name)
                                                            : (typeof item.question === 'number' || typeof item.question === 'string' ? null : item.question);
                                                        const prerequisiteLabel = item.pre && typeof item.pre === 'object'
                                                            ? (item.pre.name || item.pre.description || (item.pre.multiple_choice_model?.label ?? item.pre.multiple_choice_model?.name) || item.pre.data_type)
                                                            : null;
                                                        const questionOrPrereq = questionLabel ?? prerequisiteLabel ?? (item.question != null ? `Question #${item.question}` : '—');
                                                        const answerValue = item.answer_chosen?.option ?? item.answer_text ?? (item.pre && typeof item.pre === 'object' ? item.pre.value : null) ?? item.answer ?? item.value ?? '—';
                                                        return (
                                                            <tr key={item.id || idx}>
                                                                <td>{questionOrPrereq}</td>
                                                                <td>{answerValue}</td>
                                                            </tr>
                                                        );
                                                    })}
                                                </tbody>
                                            </table>
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SchemeApplicationDetail;
